
import { Suspense, lazy, useMemo } from "react";

const CreateMediaModal = lazy(() => import("@/components/CreateMediaModal"));
const CreateScreenModal = lazy(() => import("@/components/CreateScreenModal"));
const EditMediaModal = lazy(() => import("@/components/EditMediaModal"));
const EditScreenModal = lazy(() => import("@/components/EditScreenModal"));
const DeviceActivationModal = lazy(() => import("@/components/DeviceActivationModal"));
const CreativesModal = lazy(() => import("./CreativesModal"));
const ChangePasswordModal = lazy(() => import("./ChangePasswordModal"));
const ProfileModal = lazy(() => import("./ProfileModal"));
const PrivacyPolicyModal = lazy(() => import("./PrivacyPolicyModal"));
const RequiredInfoModal = lazy(() => import("./RequiredInfoModal"));

const MyPlanModal = lazy(() => import("@/components/dashboard/MyPlanModal").then(module => ({ default: module.MyPlanModal })));
const PaymentHistoryModal = lazy(() => import("@/components/dashboard/PaymentHistoryModal"));
const VenuePartnerModal = lazy(() => import("@/components/dashboard/VenuePartnerModal"));
const NoActiveDevicesModal = lazy(() => import("@/components/dashboard/NoActiveDevicesModal").then(module => ({ default: module.NoActiveDevicesModal })));
const PlayerScheduleModal = lazy(() => import("@/components/dashboard/PlayerScheduleModal").then(module => ({ default: module.PlayerScheduleModal })));

type DashboardModalsProps = {
    showCreateMediaModal: boolean;
    setShowCreateMediaModal: (show: boolean) => void;
    handleCreateMedia: (data: any) => Promise<void>;
    screens: any[];
    medias?: any[]; // Array de mídias para buscar dados atualizados
    showCreateScreenModal: boolean;
    setShowCreateScreenModal: (show: boolean) => void;
    handleCreateScreen: (data: any) => Promise<void>;
    showEditMediaModal: boolean;
    setShowEditMediaModal: (show: boolean) => void;
    handleUpdateMedia: (data: any) => Promise<void>;
    selectedMedia: {
        id: string;
        title: string;
        description: string;
        url: string;
        user_id: string;
        selected_screens: string[];
        display_time: number;
        created_at: string;
        updated_at: string;
        last_updated_at: string;
        active: boolean;
    } | null;
    showEditScreenModal: boolean;
    setShowEditScreenModal: (show: boolean) => void;
    handleUpdateScreen: (data: any) => Promise<void>;
    selectedScreen: any;
    showDeviceActivationModal: boolean;
    setShowDeviceActivationModal: (show: boolean) => void;
    handleCreateDevice: (code: string, screenId: string, deviceName?: string) => Promise<boolean>;
    isActivatingDevice: boolean;
    onUpgradeClick?: () => void;
    showCreativesModal: boolean;
    setShowCreativesModal: (show: boolean) => void;
    showChangePasswordModal: boolean;
    setShowChangePasswordModal: (show: boolean) => void;
    showProfileModal: boolean;
    setShowProfileModal: (show: boolean) => void;
    showPrivacyPolicyModal: boolean;
    setShowPrivacyPolicyModal: (show: boolean) => void;
    showRequiredInfoModal: boolean;
    setShowRequiredInfoModal: (show: boolean) => void;
    showMyPlanModal: boolean;
    setShowMyPlanModal: (show: boolean) => void;
    showPaymentHistoryModal: boolean;
    setShowPaymentHistoryModal: (show: boolean) => void;
    showVenuePartnerModal: boolean;
    setShowVenuePartnerModal: (show: boolean) => void;
    showNoActiveDevicesModal: boolean;
    setShowNoActiveDevicesModal: (show: boolean) => void;
    showPlayerScheduleModal: boolean;
    setShowPlayerScheduleModal: (show: boolean) => void;
}

const DashboardModals = ({
    showCreateMediaModal, setShowCreateMediaModal, handleCreateMedia, screens,
    medias = [],
    showCreateScreenModal, setShowCreateScreenModal, handleCreateScreen,
    showEditMediaModal, setShowEditMediaModal, handleUpdateMedia, selectedMedia,
    showEditScreenModal, setShowEditScreenModal, handleUpdateScreen, selectedScreen,
    showDeviceActivationModal, setShowDeviceActivationModal, handleCreateDevice, isActivatingDevice,
    onUpgradeClick,
    showCreativesModal, setShowCreativesModal,
    showChangePasswordModal, setShowChangePasswordModal,
    showProfileModal, setShowProfileModal,
    showPrivacyPolicyModal, setShowPrivacyPolicyModal,
    showRequiredInfoModal, setShowRequiredInfoModal,
    showMyPlanModal, setShowMyPlanModal,
    showPaymentHistoryModal, setShowPaymentHistoryModal,
    showVenuePartnerModal, setShowVenuePartnerModal,
    showNoActiveDevicesModal, setShowNoActiveDevicesModal,
    showPlayerScheduleModal, setShowPlayerScheduleModal
}: DashboardModalsProps) => {

    // Buscar a mídia mais atualizada do array de mídias
    // Isso garante que após uma atualização, sempre tenhamos os dados mais recentes
    const currentMedia = useMemo(() => {
        if (!selectedMedia?.id) return null;

        // Buscar a mídia atualizada do array
        const updatedMedia = medias.find(m => m.id === selectedMedia.id);

        if (updatedMedia) {
            console.log('🔄 [DashboardModals] Usando mídia atualizada do array:', {
                id: updatedMedia.id,
                selected_screens: updatedMedia.selected_screens
            });
            return updatedMedia;
        }

        // Fallback para selectedMedia se não encontrar no array
        console.log('⚠️ [DashboardModals] Mídia não encontrada no array, usando selectedMedia');
        return selectedMedia;
    }, [selectedMedia?.id, medias]);
    return (
        <>
            <Suspense fallback={null}>
                <CreateMediaModal
                    open={showCreateMediaModal}
                    onOpenChange={setShowCreateMediaModal}
                    onSubmit={handleCreateMedia}
                    screens={screens}
                />
            </Suspense>

            <Suspense fallback={null}>
                <CreateScreenModal
                    open={showCreateScreenModal}
                    onOpenChange={setShowCreateScreenModal}
                    onSubmit={handleCreateScreen}
                    existingScreens={screens}
                />
            </Suspense>

            <Suspense fallback={null}>
                <EditMediaModal
                    open={showEditMediaModal}
                    onOpenChange={setShowEditMediaModal}
                    onSubmit={handleUpdateMedia}
                    screens={screens}
                    media={currentMedia ? {
                        id: currentMedia.id,
                        title: currentMedia.title,
                        description: currentMedia.description || '',
                        url: currentMedia.url,
                        user_id: currentMedia.user_id,
                        organization_id: currentMedia.organization_id || 'fe3bd8b7-d5ad-4cb4-8ee6-cbf0a3a5b2d2',
                        selected_screens: currentMedia.selected_screens || [],
                        display_time: currentMedia.display_time || 5000,
                        created_at: currentMedia.created_at || new Date().toISOString(),
                        updated_at: currentMedia.updated_at || currentMedia.created_at || new Date().toISOString(),
                        last_updated_at: currentMedia.last_updated_at || currentMedia.updated_at || currentMedia.created_at || new Date().toISOString(),
                        active: currentMedia.active !== undefined ? currentMedia.active : true,
                    } : null}
                />
            </Suspense>

            <Suspense fallback={null}>
                <EditScreenModal
                    open={showEditScreenModal}
                    onOpenChange={setShowEditScreenModal}
                    onSubmit={handleUpdateScreen}
                    screen={selectedScreen}
                />
            </Suspense>

            <Suspense fallback={null}>
                <DeviceActivationModal
                    open={showDeviceActivationModal}
                    onOpenChange={setShowDeviceActivationModal}
                    onSubmit={handleCreateDevice}
                    screens={screens}
                    isActivating={isActivatingDevice}
                    onUpgradeClick={onUpgradeClick}
                />
            </Suspense>

            <Suspense fallback={null}>
                <CreativesModal
                    isOpen={showCreativesModal}
                    onClose={() => setShowCreativesModal(false)}
                />
            </Suspense>

            <Suspense fallback={null}>
                <ChangePasswordModal
                    open={showChangePasswordModal}
                    onOpenChange={setShowChangePasswordModal}
                />
            </Suspense>

            <Suspense fallback={null}>
                <ProfileModal
                    open={showProfileModal}
                    onOpenChange={setShowProfileModal}
                />
            </Suspense>

            <Suspense fallback={null}>
                <PrivacyPolicyModal
                    open={showPrivacyPolicyModal}
                    onOpenChange={setShowPrivacyPolicyModal}
                />
            </Suspense>

            <Suspense fallback={null}>
                <RequiredInfoModal
                    open={showRequiredInfoModal}
                    onOpenChange={setShowRequiredInfoModal}
                />
            </Suspense>

            <Suspense fallback={null}>
                <MyPlanModal
                    isOpen={showMyPlanModal}
                    onClose={() => setShowMyPlanModal(false)}
                    onRefresh={() => {
                        console.log('🔄 DashboardModals - Refresh solicitado pelo MyPlanModal');
                        window.location.reload();
                    }}
                />
            </Suspense>

            <Suspense fallback={null}>
                <PaymentHistoryModal
                    isOpen={showPaymentHistoryModal}
                    onClose={() => setShowPaymentHistoryModal(false)}
                />
            </Suspense>

            <Suspense fallback={null}>
                <VenuePartnerModal
                    isOpen={showVenuePartnerModal}
                    onClose={() => setShowVenuePartnerModal(false)}
                />
            </Suspense>

            <Suspense fallback={null}>
                <NoActiveDevicesModal
                    isOpen={showNoActiveDevicesModal}
                    onClose={() => setShowNoActiveDevicesModal(false)}
                />
            </Suspense>

            <Suspense fallback={null}>
                <PlayerScheduleModal
                    isOpen={showPlayerScheduleModal}
                    onClose={() => setShowPlayerScheduleModal(false)}
                />
            </Suspense>
        </>
    )
}

export default DashboardModals;
